/**
 * Frontend Web Vitals capture → Cloud Monitoring ingest (PLAT-314 / B9).
 *
 * Captures LCP / FID / CLS / TTFB / INP via Google's `web-vitals` library
 * and POSTs them to `core-service`'s `/v1/core/web-vitals` ingest endpoint
 * (configured via `VITE_WEB_VITALS_ENDPOINT`). Cloud Monitoring writes are
 * handled server-side; this module only buffers + transmits.
 *
 * Sampling:
 * - 100% in dev, 10% in prod per spec (controlled via
 *   `VITE_WEB_VITALS_SAMPLE_RATE`).
 * - Stable per-session: one `Math.random()` at module init decides
 *   whether THIS page-session reports anything. Preserves funnel coherence
 *   — the same user contributes LCP + INP + CLS, rather than scattered
 *   single metrics from different sessions.
 *
 * Transmission:
 * - Events buffered in-memory (no localStorage — backend is the source of
 *   truth for any persisted state).
 * - Flushed on `visibilitychange === "hidden"` AND `pagehide` via
 *   `navigator.sendBeacon` so terminal events survive tab close (matches
 *   web-vitals authors' published guidance).
 * - Falls back to `fetch({ keepalive: true })` when sendBeacon is missing
 *   or rejects (e.g., payload >64KB — won't happen at these sizes).
 *
 * The endpoint accepts public traffic with IP rate-limiting; no auth header
 * is sent because Web Vitals fire on cold-start before any auth state
 * exists, especially on /public/run/:token and /role-interview/:token.
 */

import {
  onCLS,
  onFCP,
  onFID,
  onINP,
  onLCP,
  onTTFB,
  type Metric,
} from "web-vitals";
import { getEnv } from "@/config/env";


interface BufferedEvent {
  metric: string;
  value: number;
  route: string;
  navigationType?: string;
  deviceClass?: "mobile" | "tablet" | "desktop";
  rating?: "good" | "needs-improvement" | "poor";
}

interface Batch {
  events: BufferedEvent[];
  sessionId: string;
}

// Module state — wrapped in getters so tests can reset via `__resetForTest`.
let initialized = false;
let sampledIn = false;
let sessionId = "";
let endpoint = "";
let buffer: BufferedEvent[] = [];

/** Stable per-tab session id. Not persisted. */
function newSessionId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Same normalization table as `core-service/src/routes/webVitals.routes.ts`.
 * Server normalizes too (defense in depth), but doing it client-side keeps
 * the network payload tight and obviously-correct on the way out.
 */
const ROUTE_PATTERNS: Array<{ test: RegExp; label: string }> = [
  { test: /^\/public\/run\/[^/]+$/, label: "/public/run/:token" },
  { test: /^\/role-interview\/[^/]+$/, label: "/role-interview/:token" },
  { test: /^\/task-interview\/[^/]+$/, label: "/task-interview/:token" },

  { test: /^\/discover$/, label: "/discover" },
  { test: /^\/interviews$/, label: "/interviews" },

  {
    test: /^\/research\/company\/[^/]+(\/[^/]+)?$/,
    label: "/research/company/:id",
  },
  {
    test: /^\/research\/run\/[^/]+(\/[^/]+)?(\/opportunities\/[^/]+)?$/,
    label: "/research/run/:id",
  },
  {
    test: /^\/research\/companies$/,
    label: "/research/companies",
  },
  { test: /^\/research$/, label: "/research" },

  // PLAT-450 — merged Interviews hub (retired document + design
  // families redirect here; their labels are gone from new clients).
  {
    test: /^\/interviews\/templates\/create$/,
    label: "/interviews/templates",
  },
  {
    test: /^\/interviews\/templates\/[^/]+\/(edit|sandbox)$/,
    label: "/interviews/templates",
  },
  {
    test: /^\/interviews\/templates$/,
    label: "/interviews/templates",
  },
  {
    test: /^\/interviews\/workflows(\/create|\/[^/]+\/(edit|sandbox))?$/,
    label: "/interviews/workflows",
  },
  { test: /^\/interviews\/all$/, label: "/interviews/all" },
  {
    test: /^\/interviews\/role\/start\/batch$/,
    label: "/interviews/role/batch-start",
  },
  {
    test: /^\/interviews\/role\/batch\/[^/]+$/,
    label: "/interviews/role/batch/:id",
  },
  {
    test: /^\/interviews\/role\/[^/]+\/spd$/,
    label: "/interviews/role/:id/spd",
  },
  {
    test: /^\/interviews\/[^/]+\/start$/,
    label: "/interviews/:mode/start",
  },
  {
    test: /^\/interviews\/[^/]+\/[^/]+\/review$/,
    label: "/interviews/:mode/:id/review",
  },
  {
    test: /^\/interviews\/[^/]+\/[^/]+\/context$/,
    label: "/interviews/:mode/:id/context",
  },
  {
    test: /^\/interviews\/[^/]+\/[^/]+$/,
    label: "/interviews/:mode/:id",
  },

  {
    test: /^\/org\/unit\/[^/]+\/role\/[^/]+$/,
    label: "/org/unit/:id/role/:slug",
  },
  { test: /^\/org\/unit\/[^/]+$/, label: "/org/unit/:id" },
  { test: /^\/org$/, label: "/org" },

  { test: /^\/settings\/[^/]+$/, label: "/settings/:tab" },
  { test: /^\/settings$/, label: "/settings" },
];

export function normalizeRoute(path: string): string {
  if (typeof path !== "string" || path.length === 0) return "other";
  const cleaned = path.split("?")[0].split("#")[0];
  for (const { test, label } of ROUTE_PATTERNS) {
    if (test.test(cleaned)) return label;
  }
  return "other";
}

/** UA-sniff for a coarse device-class label. Bounded cardinality (3 values). */
function detectDeviceClass(): "mobile" | "tablet" | "desktop" {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/Tablet|iPad/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return "mobile";
  return "desktop";
}

function recordMetric(m: Metric): void {
  if (!sampledIn) return;
  buffer.push({
    // Wire contract is lowercase — matches Cloud Monitoring metric path
    // (`custom.googleapis.com/web_vitals/lcp`) and core-service's
    // ALLOWED_METRICS set in webVitals.routes.ts.
    metric: m.name.toLowerCase(),
    value: m.value,
    route: normalizeRoute(
      typeof window !== "undefined" ? window.location.pathname : "",
    ),
    navigationType: m.navigationType,
    deviceClass: detectDeviceClass(),
    rating: m.rating,
  });
}

function buildBatchPayload(): Batch | null {
  if (buffer.length === 0) return null;
  const batch: Batch = { events: buffer.slice(), sessionId };
  buffer = [];
  return batch;
}

/** Send buffered events. Uses sendBeacon for terminal flushes; fetch otherwise. */
function flush(): void {
  if (!endpoint) return;
  const batch = buildBatchPayload();
  if (!batch) return;
  const body = JSON.stringify(batch);

  let sentViaBeacon = false;
  if (
    typeof navigator !== "undefined" &&
    typeof navigator.sendBeacon === "function"
  ) {
    try {
      const blob = new Blob([body], { type: "application/json" });
      sentViaBeacon = navigator.sendBeacon(endpoint, blob);
    } catch {
      sentViaBeacon = false;
    }
  }

  if (!sentViaBeacon && typeof fetch === "function") {
    // Best-effort. `keepalive` lets the request outlive the page unload.
    fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {
      // Silent — telemetry MUST NOT break the app.
    });
  }
}

/**
 * Initialize Web Vitals capture. Idempotent — multiple calls no-op after
 * the first. Reads `VITE_WEB_VITALS_ENDPOINT` (required) and
 * `VITE_WEB_VITALS_SAMPLE_RATE` (number 0..1; required).
 */
export function initWebVitals(): void {
  if (initialized) return;
  if (typeof window === "undefined") return;

  const ep = getEnv("VITE_WEB_VITALS_ENDPOINT");
  if (!ep) return;

  const rateRaw = getEnv("VITE_WEB_VITALS_SAMPLE_RATE");
  const rate = rateRaw === undefined ? NaN : Number(rateRaw);
  if (!Number.isFinite(rate) || rate <= 0) return;

  initialized = true;
  endpoint = ep;
  sessionId = newSessionId();
  sampledIn = Math.random() < Math.min(rate, 1);

  if (!sampledIn) return;

  // Subscribe to all five Core Web Vitals + FCP for context. FCP is not
  // strictly in B9's metric list, but it's free to capture (same library
  // call) and is useful when LCP is regressed — tells you whether the
  // problem is paint timing or LCP-element selection.
  onLCP(recordMetric);
  onFID(recordMetric);
  onCLS(recordMetric);
  onTTFB(recordMetric);
  onINP(recordMetric);
  onFCP(recordMetric);

  // Flush on terminal lifecycle events. visibilitychange→hidden is the
  // recommended trigger (per web-vitals authors); pagehide is a belt-and-
  // suspenders fallback for Safari quirks around tab-restore.
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", flush);
}

/** Test-only. Resets module state so tests can re-init cleanly. */
export function __resetForTest(): void {
  initialized = false;
  sampledIn = false;
  sessionId = "";
  endpoint = "";
  buffer = [];
}

/** Test-only. Direct buffer access for assertion. */
export function __getBufferForTest(): BufferedEvent[] {
  return buffer.slice();
}

/** Test-only. Direct flush trigger. */
export function __flushForTest(): void {
  flush();
}
